// scheduled-api.ts — REST wrappers for the manager's /scheduled-chats control plane
// (migration 017). Mirrors api.ts: identity is the Auth0 bearer (authHeaders), errors
// surface via jsonOrThrow. A "scheduled chat" is a saved prompt that re-runs itself on
// a schedule; each fire is a real chat (a "run").

import { API_ORIGIN, authHeaders, jsonOrThrow } from './api';

export type ScheduleFreq = 'hourly' | 'daily' | 'weekly' | 'monthly';

// Every-N cadence (migration 023): "Every weekday at 08:00", "Every 2 weeks on
// Monday". `weekday` (Mon–Fri) is a first-class unit, not a flag.
export type EveryUnit = 'hour' | 'day' | 'weekday' | 'week' | 'month';

// Model tiers (historically "brain" on the wire). The UI shows real model names
// with plain hints; the server maps tiers onto provider/model/effort in ONE
// place (backend/manager/src/brain-tiers.ts).
export type BrainTier = 'fast' | 'balanced' | 'max';

export type ScheduledChat = {
	id: string;
	name: string;
	prompt: string;
	duty: string | null; // plain-speech duty sentence; null ⇒ derive client-side
	freq: ScheduleFreq;
	interval: number;
	unit: EveryUnit;
	atHour: number;
	atMinute: number;
	weekday: number | null;
	monthday: number | null;
	tz: string;
	brain: BrainTier | null; // null ⇒ default routing (renders as Balanced)
	uses: string[] | null; // integration allowlist (capability ids); null ⇒ unrestricted
	deliverInapp: boolean;
	deliverSlack: boolean;
	// Slack channel target (both-or-neither non-null). When set, the same report
	// card is also posted to that channel, independent of the DM toggle.
	slackChannelId: string | null;
	slackChannelName: string | null; // display name WITHOUT a leading '#'
	conditional: boolean;
	weekdaysOnly: boolean;
	runUntil: string | null; // "YYYY-MM-DD" in tz, or null (runs indefinitely)
	enabled: boolean;
	schedule: string; // human cadence text, e.g. "Every Monday at 08:00"
	nextRunAt: string; // ISO
	lastRunAt: string | null;
	createdAt: string;
	lastRunStatus?: 'running' | 'ok' | 'failed' | 'skipped' | null;
	lastRunSessionId?: string | null;
};

export type ScheduledRun = {
	id: string;
	sessionId: string | null;
	runAt: string;
	status: 'running' | 'ok' | 'failed' | 'skipped';
	error: string | null;
	summary: string | null;
	finishedAt: string | null;
};

export type ScheduleInput = {
	name: string;
	prompt: string;
	duty?: string | null;
	interval: number;
	unit: EveryUnit;
	atHour: number;
	atMinute: number;
	weekday?: number | null;
	monthday?: number | null;
	tz?: string;
	brain?: BrainTier | null;
	uses?: string[] | null;
	weekdaysOnly?: boolean; // hour unit only (Mon–Fri hourly)
	deliverInapp: boolean;
	deliverSlack: boolean;
	// Both-or-neither non-null; explicit null clears; OMITTED on PATCH keeps the
	// current value (same keep-if-omitted semantics as duty/brain/uses).
	slackChannelId?: string | null;
	slackChannelName?: string | null;
	conditional: boolean;
	runUntil?: string | null; // "YYYY-MM-DD" in tz, or null/empty for indefinite
};

// ── The Studio proposal turn ──────────────────────────────────────────────────
// One assistant round-trip that turns a plain request into a VALIDATED draft, or
// a follow-up question with answer chips (never a guessed cadence).

export type ProposalCadence = {
	interval: number;
	unit: EveryUnit;
	weekday: number | null;
	monthday: number | null;
	atHour: number;
	atMinute: number;
};

export type RoutineDraft = {
	name: string;
	duty: string;
	prompt: string;
	cadence: ProposalCadence;
	uses: string[];
	brain: BrainTier;
	deliverSlack: boolean;
	conditional: boolean;
	// Never set by the proposal turn; the UI may pick a channel before create.
	slackChannelId?: string | null;
	slackChannelName?: string | null;
	// Never set by the proposal turn (defaults true); the UI's delivery editor
	// may switch it off before create when a Slack destination is on.
	deliverInapp?: boolean;
};

export type ProposalResponse =
	| { kind: 'proposal'; draft: RoutineDraft }
	| { kind: 'question'; question: string; options: string[] };

export type Clarification = { question: string; answer: string };

const base = (): string => `${API_ORIGIN}/scheduled-chats`;

async function jsonHeaders(): Promise<Record<string, string>> {
	return { ...(await authHeaders()), 'content-type': 'application/json' };
}

export async function listScheduledChats(): Promise<ScheduledChat[]> {
	const res = await fetch(base(), { headers: await authHeaders() });
	const body = await jsonOrThrow<{ scheduledChats: ScheduledChat[] }>(res, 'list scheduled chats');
	return body.scheduledChats;
}

export async function getScheduledChat(id: string): Promise<ScheduledChat> {
	const res = await fetch(`${base()}/${id}`, { headers: await authHeaders() });
	return jsonOrThrow<ScheduledChat>(res, 'get scheduled chat');
}

export async function createScheduledChat(input: ScheduleInput): Promise<ScheduledChat> {
	const res = await fetch(base(), { method: 'POST', headers: await jsonHeaders(), body: JSON.stringify(input) });
	return jsonOrThrow<ScheduledChat>(res, 'create scheduled chat');
}

export async function updateScheduledChat(
	id: string,
	input: ScheduleInput & { enabled?: boolean },
): Promise<ScheduledChat> {
	const res = await fetch(`${base()}/${id}`, { method: 'PATCH', headers: await jsonHeaders(), body: JSON.stringify(input) });
	return jsonOrThrow<ScheduledChat>(res, 'update scheduled chat');
}

export async function setScheduledChatEnabled(id: string, enabled: boolean): Promise<ScheduledChat> {
	const res = await fetch(`${base()}/${id}`, {
		method: 'PATCH',
		headers: await jsonHeaders(),
		body: JSON.stringify({ enabled }),
	});
	return jsonOrThrow<ScheduledChat>(res, 'toggle scheduled chat');
}

export async function deleteScheduledChat(id: string): Promise<void> {
	const res = await fetch(`${base()}/${id}`, { method: 'DELETE', headers: await authHeaders() });
	if (!res.ok && res.status !== 204) await jsonOrThrow(res, 'delete scheduled chat');
}

export async function runScheduledChatNow(id: string): Promise<{ sessionId: string; runId: string }> {
	const res = await fetch(`${base()}/${id}/run`, { method: 'POST', headers: await authHeaders() });
	return jsonOrThrow<{ sessionId: string; runId: string }>(res, 'run scheduled chat');
}

export async function listScheduledRuns(id: string): Promise<ScheduledRun[]> {
	const res = await fetch(`${base()}/${id}/runs`, { headers: await authHeaders() });
	const body = await jsonOrThrow<{ runs: ScheduledRun[] }>(res, 'list runs');
	return body.runs;
}

// One assistant round-trip → a structured schedule draft or a follow-up question.
// Slow-ish (a real headless turn in the user's container) — callers show the
// "Drafting a schedule…" state while this is in flight.
export async function proposeSchedule(
	request: string,
	clarifications: Clarification[] = [],
): Promise<ProposalResponse> {
	const res = await fetch(`${base()}/propose`, {
		method: 'POST',
		headers: await jsonHeaders(),
		body: JSON.stringify({ request, clarifications }),
	});
	return jsonOrThrow<ProposalResponse>(res, 'draft schedule');
}

// ── Slack channels (delivery target picker) ──────────────────────────────────

export type SlackChannel = { id: string; name: string; isPrivate: boolean };
export type SlackChannelsInfo = { available: boolean; reason?: string; channels: SlackChannel[] };

// GET /slack/channels — always HTTP 200; available:false carries a
// human-readable reason the UI shows verbatim.
export async function listSlackChannels(): Promise<SlackChannelsInfo> {
	const res = await fetch(`${API_ORIGIN}/slack/channels`, { headers: await authHeaders() });
	return jsonOrThrow<SlackChannelsInfo>(res, 'list slack channels');
}
